import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { resetTranscriptState } from 'actions/shareVideo';
import {
  fetchTranscriptPhrases,
  subscribeToTranscriptStatus,
} from 'creators/shareVideo';

import {
  selectActiveTranscriptId,
  selectTranscript,
  selectUserCanAccessTranscript,
} from 'selectors/transcripts';
import { selectSendingNewTrim } from 'selectors/trimming';
import { selectVideoPlayerPassword } from 'selectors/videoPlayer';

const FetchTranscript = (): null => {
  const dispatch = useDispatch();

  const [subscription, setSubscription] = useState(null);
  const [didFirstFetch, setDidFirstFetch] = useState(false);
  const [fetchTranscriptError, setFetchTranscriptError] = useState(null);

  const sendingNewTrim = useSelector(selectSendingNewTrim);
  const transcript = useSelector(selectTranscript);
  const userCanAccessTranscript = useSelector(selectUserCanAccessTranscript);
  const activeTranscriptId = useSelector(selectActiveTranscriptId);
  const videoPlayerPassword = useSelector(selectVideoPlayerPassword);

  // Initial fetching
  useEffect(() => {
    if (activeTranscriptId && userCanAccessTranscript && !didFirstFetch) {
      const d: any = dispatch(fetchTranscriptPhrases(videoPlayerPassword));

      d.catch(err => {
        setFetchTranscriptError(err);
        console.error('unable to fetch initial transcription', err);
      }).finally(() => {
        setDidFirstFetch(true);
      });
    }
  }, [
    activeTranscriptId,
    didFirstFetch,
    dispatch,
    userCanAccessTranscript,
    videoPlayerPassword,
  ]);

  // Subscribes and watches for transcription status changes
  useEffect(() => {
    if (
      activeTranscriptId &&
      userCanAccessTranscript &&
      didFirstFetch &&
      !transcript &&
      !subscription &&
      !fetchTranscriptError
    ) {
      const subscription: any = dispatch(
        subscribeToTranscriptStatus(videoPlayerPassword)
      );

      if (subscription) {
        setSubscription(subscription);
      }
    }

    return () => {
      subscription?.unsubscribe();
    };
  }, [
    activeTranscriptId,
    didFirstFetch,
    dispatch,
    subscription,
    transcript,
    userCanAccessTranscript,
    fetchTranscriptError,
    videoPlayerPassword,
  ]);

  // makes sure we unsubscribe from pub/sub if we get a new transcript
  useEffect(() => {
    if (transcript && subscription) {
      subscription.unsubscribe();
      setSubscription(null);
    }
  }, [transcript, subscription]);

  // Watches for trim changes. Resets the transcription state
  // which will trigger the other effect of subscribing for changes
  useEffect(
    () => {
      if (!didFirstFetch) {
        return;
      }

      if (!sendingNewTrim) {
        dispatch(resetTranscriptState());
      }
    },
    // We don't care if 'didFirstFetch' changed so we are omitting it from
    // the list to prevent 'resetTranscriptState' to be called excessively
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, sendingNewTrim]
  );

  return null;
};

export default FetchTranscript;
